<template>
  <div class="opportunity-lookup-result-ui">
    <VHeading :level="2" class="opportunity-lookup">Opportunity Details</VHeading>

    <OpportunityLookupResultSection>
      <OpportunityLookupResultField
        label="Customer reference"
        :value="opportunity.customer_reference"
        has-copy-btn
      />
      <OpportunityLookupResultField label="Entity type" :value="entity" />
      <OpportunityLookupResultField label="CSV Source" :value="opportunity.source" />
      <OpportunityLookupResultField label="Is archived?" :value="archived" />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="Address matching results">
      <OpportunityLookupResultField
        label="Input address (From CRM)"
        :value="opportunity.matched_input_address"
      />

      <OpportunityLookupResultField
        label="Matched property address"
        :value="opportunity.property_address"
      />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="CRM Dates">
      <OpportunityLookupResultField label="Record created" :value="recordCreatedDate" />
      <OpportunityLookupResultField label="Last updated" :value="lastRecordUpdatedDate" />
      <OpportunityLookupResultField label="Sales valuation" :value="salesValuationDate" />
      <OpportunityLookupResultField label="Lettings valuation" :value="lettingsValuationDate" />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="Internal Dates">
      <OpportunityLookupResultField label="Created at" :value="createdAt" />
      <OpportunityLookupResultField label="Updated at" :value="updatedAt" />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="Listings" has-outline>
      <OpportunityLookupResultListings :data="opportunity.listings" />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="Appointments" has-outline>
      <OpportunityLookupResultAppointments :data="opportunity.appointments" />
    </OpportunityLookupResultSection>

    <OpportunityLookupResultSection title="External events" has-outline>
      <OpportunityLookupResultExternalEvents :data="opportunity.external_events" />
    </OpportunityLookupResultSection>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TManagerCompanyOpportunityLookup } from '~/modules/manager/types';

  // Utils
  import { DateTimeFormatter } from '~/misc/utils/datetime';
  import { capitalizeFirstWordLetter } from '~/misc/utils/text';

  // Components
  import OpportunityLookupResultListings from '~/modules/manager/company/opportunity-lookup/result/listings/OpportunityLookupResultListings.vue';
  import OpportunityLookupResultAppointments from '~/modules/manager/company/opportunity-lookup/result/appointments/OpportunityLookupResultAppointments.vue';
  import OpportunityLookupResultExternalEvents from '~/modules/manager/company/opportunity-lookup/result/external-events/OpportunityLookupResultExternalEvents.vue';
  import OpportunityLookupResultSection from '~/modules/manager/company/opportunity-lookup/result/section/OpportunityLookupResultSection.vue';
  import OpportunityLookupResultField from '~/modules/manager/company/opportunity-lookup/result/field/OpportunityLookupResultField.vue';
  import VHeading from '~/misc/v-heading/VHeading.vue';

  const props = defineProps({
    opportunity: {
      type: Object as PropType<TManagerCompanyOpportunityLookup>,
      required: true,
    },
  });

  const recordCreatedDate: ComputedRef<string> = computed<string>(() =>
    props.opportunity.record_created_date
      ? DateTimeFormatter.toShortDate(props.opportunity.record_created_date)
      : '-'
  );

  const lastRecordUpdatedDate: ComputedRef<string> = computed<string>(() =>
    props.opportunity.last_record_updated_date
      ? DateTimeFormatter.toShortDate(props.opportunity.last_record_updated_date)
      : '-'
  );

  const salesValuationDate: ComputedRef<string> = computed<string>(() =>
    props.opportunity.sales_valuation_date
      ? DateTimeFormatter.toShortDate(props.opportunity.sales_valuation_date)
      : '-'
  );

  const lettingsValuationDate: ComputedRef<string> = computed<string>(() =>
    props.opportunity.lettings_valuation_date
      ? DateTimeFormatter.toShortDate(props.opportunity.lettings_valuation_date)
      : '-'
  );

  const createdAt: ComputedRef<string> = computed<string>(
    () => DateTimeFormatter.toDateTime(props.opportunity.created_at) || '-'
  );

  const updatedAt: ComputedRef<string> = computed<string>(
    () => DateTimeFormatter.toDateTime(props.opportunity.updated_at) || '-'
  );

  const archived: ComputedRef<string> = computed<string>(() =>
    props.opportunity.archived ? 'Yes' : 'No'
  );

  const entity: ComputedRef<string> = computed<string>(() =>
    props.opportunity.entity ? capitalizeFirstWordLetter(props.opportunity.entity) : '-'
  );
</script>
